import { createBrowserRouter, Navigate, useLocation, useParams } from 'react-router';
import * as Sentry from '@sentry/react';
import { RootLayout } from './components/layout/RootLayout';
import { NotFoundPage } from './pages/NotFoundPage';
import { RouteErrorPage } from './pages/RouteErrorPage';
import { MatchesRedirect, StandingsRedirect } from './pages/TournamentAnchorRedirect';

// Wrap createBrowserRouter so Sentry names navigation transactions by their
// parameterized route path (e.g. /t/:slug) instead of raw URLs. Pairs with
// browserTracingIntegration() in instrument.ts.
const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

/**
 * Redirects the legacy unified-summary URL to the canonical tournament hub.
 * QR codes and shared links use /t/:slug; everything else should too.
 */
function LegacySummaryRedirect() {
  const { slug } = useParams<{ slug: string }>();
  const { search } = useLocation();
  return <Navigate to={`/t/${slug}${search}`} replace />;
}

/**
 * Application router configuration.
 * 
 * Routes are organized by feature area:
 * - / : Home (tournament discovery)
 * - /sign-in : Authentication (email OTP and password)
 * - /profile : Player profile
 * - /tournament/:id : Tournament detail and dashboard
 * - /td : Tournament Director dashboard
 * - /admin : Admin dashboard and club management
 */
export const router = sentryCreateBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    errorElement: <RouteErrorPage />,
    children: [
      {
        index: true,
        lazy: () => import('./pages/RootIndexPage'),
      },
      {
        path: 'faq',
        lazy: () => import('./pages/FaqPage'),
      },
      // Direct pitch page for club owners/TDs (shared/DM'd individually,
      // distinct from the general public landing page at '/').
      {
        path: 'for-organisers',
        lazy: () => import('./pages/ForOrganisersPage'),
      },
      // Player-facing pitch page (parallel to for-organisers).
      {
        path: 'for-players',
        lazy: () => import('./pages/ForPlayersPage'),
      },
      // Standalone public home scorecard — no signup required.
      {
        path: 'play',
        lazy: () => import('./pages/PlayPage'),
      },
      // Auth routes
      {
        path: 'sign-in',
        lazy: () => import('./pages/SignInPage'),
      },
      // Walk-in claim landing page. Claim emails have linked here since the
      // feature shipped; the route never existed, so every link 404'd.
      {
        path: 'claim-profile',
        lazy: () => import('./pages/ClaimProfilePage'),
      },
      // Profile routes (to be implemented in Task 4)
      {
        path: 'profile',
        lazy: () => import('./pages/ProfilePage'),
      },
      // Player Bey Box (beyblade-combo-tracking) — build/edit/delete saved beys
      {
        path: 'beys',
        lazy: () => import('./pages/BeyBoxPage'),
      },
      // Legacy unified-summary URL - redirects to the canonical hub at /t/:slug
      // NOTE: This route MUST come before tournament/:tournamentId to avoid route conflicts
      {
        path: 'tournament/:slug/summary',
        element: <LegacySummaryRedirect />,
      },
      // Tournament routes (to be implemented in Tasks 5-8)
      {
        path: 'tournament/:tournamentId',
        lazy: () => import('./pages/TournamentPage'),
      },
      // The match list and standings are sections of the hub now, so these
      // two URLs redirect to their anchors rather than loading pages of their
      // own. They stay because bookmarks, shared links and older in-app links
      // still point at them.
      {
        path: 'tournament/:tournamentId/matches',
        element: <MatchesRedirect />,
      },
      {
        path: 'tournament/:tournamentId/standings',
        element: <StandingsRedirect />,
      },
      // Per-player finish-type stats — the one part of the old standings page
      // the hub doesn't carry (it needs the authenticated stats query).
      {
        path: 'tournament/:tournamentId/stats',
        lazy: () => import('./pages/PlayerStatsPage'),
      },
      // Deck registration (beyblade-combo-tracking) — player registers their
      // 3-bey deck for a combo-enabled tournament (Requirements 5.2, 5.3, 5.4)
      {
        path: 'tournament/:tournamentId/register-deck',
        lazy: () => import('./pages/RegisterDeckPage'),
      },
      // Bracket route (Requirements 9a.1-9a.7)
      {
        path: 'tournament/:tournamentId/bracket',
        lazy: () => import('./pages/BracketPage'),
      },
      // TD Bracket View - Interactive bracket with new visualization (Bracket Visualization Enhancement spec)
      // Requirements: 2.1, 2.5, 6.1, 6.2, 6.4
      {
        path: 'tournament/:tournamentId/bracket-view',
        lazy: () => import('./pages/TDBracketPage'),
      },
      // Match Detail route (TD context)
      {
        path: 'tournament/:tournamentId/match/:matchId',
        lazy: () => import('./pages/MatchDetailPage'),
      },
      // Ref Mode route (Ref Mode spec Requirements 1.4, 1.5)
      {
        path: 'tournament/:tournamentId/match/:matchId/ref',
        lazy: () => import('./pages/RefModePage'),
      },
      // TD Dashboard routes (TD Dashboard spec)
      {
        path: 'td',
        lazy: () => import('./pages/td/TDHomePage'),
      },
      {
        path: 'td/tournament/new',
        lazy: () => import('./pages/td/CreateTournamentPage'),
      },
      {
        path: 'td/tournament/:tournamentId',
        lazy: () => import('./pages/td/TDDashboardPage'),
      },
      {
        path: 'td/tournament/:tournamentId/config',
        lazy: () => import('./pages/td/TournamentConfigPage'),
      },
      {
        path: 'td/tournament/:tournamentId/players',
        lazy: () => import('./pages/td/PlayerManagementPage'),
      },
      {
        path: 'td/tournament/:tournamentId/bracket',
        lazy: () => import('./pages/td/BracketManagementPage'),
      },
      // Walk-In Management page (Enhanced Walk-In Registration spec)
      // Requirements: 8.1, 8.2, 8.3, 8.4, 8.5, 8.6, 5.1
      {
        path: 'td/walkins',
        lazy: () => import('./pages/td/WalkInManagementPage'),
      },
      // Canonical tournament hub (QR codes, shared links, in-app cards).
      // One role-aware page for spectators, players, refs, and TDs.
      // Supports ?wall=true for wall displays and legacy ?tab= deep links.
      {
        path: 't/:slug',
        lazy: () => import('./pages/TournamentSummaryPage'),
      },
      // Public Bracket View - Read-only bracket with new visualization (Bracket Visualization Enhancement spec)
      // Requirements: 2.2, 2.5, 6.1, 6.2, 6.4
      {
        path: 'public/tournament/:tournamentId/bracket',
        lazy: () => import('./pages/PublicBracketPage'),
      },
      // Public Match Detail route (public context)
      {
        path: 'public/tournament/:tournamentId/match/:matchId',
        lazy: () => import('./pages/PublicMatchDetailPage'),
      },
      // Wall Display Bracket View - Large-screen optimized bracket (Bracket Visualization Enhancement spec)
      // Requirements: 2.3, 4.1, 4.2, 4.3, 4.4, 4.5, 7.4
      {
        path: 'wall/:tournamentId/bracket',
        lazy: () => import('./pages/WallDisplayBracketPage'),
      },
      // Club Discovery routes (Club Discovery spec)
      {
        path: 'clubs',
        lazy: () => import('./pages/ClubDiscoveryPage'),
      },
      {
        path: 'clubs/request',
        lazy: () => import('./pages/RequestClubPage'),
      },
      {
        path: 'clubs/:slug',
        lazy: () => import('./pages/ClubDetailPage'),
      },
      {
        path: 'clubs/join/:code',
        lazy: () => import('./pages/JoinByCodePage'),
      },
      // Season routes (Enhanced Stats spec)
      // Requirements: 3.1-3.7, 4.1-4.8, 5.1-5.4
      {
        path: 'season/:seasonId',
        lazy: () => import('./pages/SeasonDetailPage'),
      },
      // Public season view (no auth required) - Requirements 5.1-5.4
      {
        path: 's/:seasonId',
        lazy: () => import('./pages/PublicSeasonPage'),
      },
      // Admin routes (Admin Club Management spec)
      // All admin pages include AdminRouteGuard internally for protection
      // Requirements: 6.2, 6.3, 6.4, 6.5
      {
        path: 'admin',
        lazy: () => import('./pages/admin/AdminDashboardPage'),
      },
      {
        path: 'admin/clubs',
        lazy: () => import('./pages/admin/AdminClubListPage'),
      },
      {
        path: 'admin/clubs/new',
        lazy: () => import('./pages/admin/AdminClubCreatePage'),
      },
      {
        path: 'admin/clubs/:clubId/edit',
        lazy: () => import('./pages/admin/AdminClubEditPage'),
      },
      // Dev/test-only bracket fixture harness (Bracket Visualization V2 —
      // Package 0). Registered only in non-production builds; the dead branch
      // and its lazy import are constant-folded/tree-shaken out of production
      // by Vite (import.meta.env.PROD). The page component also self-guards.
      ...(import.meta.env.PROD
        ? []
        : [
            {
              path: 'dev/bracket-fixtures',
              lazy: () => import('./pages/dev/BracketFixtureHarnessPage'),
            },
            {
              path: 'dev/combo-builder',
              lazy: () => import('./pages/dev/ComboBuilderHarnessPage'),
            },
            {
              path: 'dev/bulk-add',
              lazy: () => import('./pages/dev/BulkAddHarnessPage'),
            },
            {
              path: 'dev/season-ranking',
              lazy: () => import('./pages/dev/SeasonRankingHarnessPage'),
            },
            {
              path: 'dev/discord-export',
              lazy: () => import('./pages/dev/DiscordExportHarnessPage'),
            },
            {
              path: 'dev/career-profile',
              lazy: () => import('./pages/dev/CareerProfileHarnessPage'),
            },
          ]),
      // Catch-all for 404
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
]);
